import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'bg-brand-green text-white border-transparent',
        secondary: 'border-transparent bg-primary text-white',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground',
        outline: 'text-foreground bg-background',
        'filter-count':
          'absolute flex h-4 min-w-4 items-center justify-center rounded-full border-brand-green bg-white/80 px-1 py-0 text-[0.6875rem] font-semibold text-brand-green shadow-none',
        'notification-count':
          'flex h-4 min-w-4 items-center justify-center rounded-full p-0.5 text-[0.6875rem] bg-brand-green text-white border-transparent',
        'profile-skill-filled':
          'bg-brand-green text-white border-transparent text-sm rounded-full',
        'profile-skill-outline':
          'border-brand-green text-sm rounded-full text-brand-green',
        'profile-coins':
          'bg-brand-yellow text-yellow-900 border-transparent text-sm rounded-full font-medium shadow-sm',
        'profile-coins-soft':
          'bg-yellow-100 text-yellow-900 border-transparent text-sm rounded-full font-medium',
        'profile-coins-outline':
          'border-brand-yellow text-brand-yellow bg-white text-sm rounded-full font-medium hover:bg-brand-yellow transition-colors',
        'admin-status-new':
          'bg-[#1E90FF] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-status-open':
          'bg-[#28A745] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-status-in-progress':
          'bg-[#6F42C1] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-status-pending':
          'bg-[#FFC107] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-status-resolved':
          'bg-[#6C757D] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-status-closed':
          'bg-[#9CA3AF] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-status-inactive':
          'bg-[#6C757D] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-status-trash':
          'bg-[#FF8C00] text-white border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-priority-high':
          'bg-[#F9E9EA] text-[#C32629] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-priority-moderate':
          'bg-[#FDF8E8] text-[#EEB817] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-priority-low':
          'bg-[#E7F4E8] text-[#0E941E] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-role-admin':
          'bg-[#EBF4FF] text-[#2563EB] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-role-user':
          'bg-[#E7F4E8] text-[#0E941E] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-user-active':
          'bg-[#E8F6ED] text-[#16A34A] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-user-banned':
          'bg-[#FCEAEA] text-[#DC2626] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-user-inactive':
          'bg-[#FDF8E7] text-[#EAB308] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-user-flagged':
          'bg-[#FFF1E8] text-[#F97316] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-user-trashed':
          'bg-[#4B5563]/10 text-[#4B5563] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'admin-user-suspended':
          'bg-[#F5EBFD] text-[#9333EA] border-transparent rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'moderation-appeal-pending':
          'bg-[#FDF8E8] text-[#EEB817] border-[#FAEBBF] rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'moderation-appeal-under-review':
          'bg-[#EBF3FB] text-[#3486D7] border-[#C6DDF4] rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'flagged-item-pending':
          'bg-[#FDF8E8] text-[#EEB817] border-[#FAEBBF] rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'flagged-item-under-investigation':
          'bg-[#EDEEF0] text-[#4B5563] border-[#CDCFD4] rounded-full px-2 py-1.5 text-[0.625rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'reported-item-type':
          'bg-[#F3F4F6] text-[#3D4151] border-transparent rounded-full px-2.5 py-1.5 text-[0.6875rem] leading-none tracking-[-0.01rem] font-medium text-center whitespace-nowrap',
        'report-reason':
          'bg-[#F9E9EA] text-[#C32629] border-transparent rounded-full px-2 py-0.5 text-[0.625rem] font-medium',
      },
      size: {
        'notification-lg': 'h-5 min-w-5',
        'notification-xl': 'h-6 min-w-6 text-sm p-1',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
export type BadgeVariant = NonNullable<
  VariantProps<typeof badgeVariants>['variant']
>;
